import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Users,
  FileText,
  GraduationCap,
  DollarSign,
  Crown,
  Shield,
  Search,
  Plus,
  Settings,
  TrendingUp,
  CreditCard
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, isAdmin, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [showCreditsDialog, setShowCreditsDialog] = useState(false);
  const [newPlan, setNewPlan] = useState('');
  const [creditsToAdd, setCreditsToAdd] = useState(10);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchStats();
    fetchUsers();
  }, [isAdmin, navigate]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: getAuthHeader()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching admin stats:', error);
      toast.error('Failed to load admin stats');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: getAuthHeader()
      });
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePlan = async () => {
    if (!selectedUser || !newPlan) return;
    
    try {
      await axios.put(
        `${API}/admin/users/${selectedUser.id}/subscription?plan=${newPlan}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(`Updated ${selectedUser.name}'s plan to ${newPlan}`);
      setShowPlanDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update plan');
    }
  };

  const handleAddCredits = async () => {
    if (!selectedUser || creditsToAdd <= 0) return;
    
    try {
      await axios.put(
        `${API}/admin/users/${selectedUser.id}/credits?credits=${creditsToAdd}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(`Added ${creditsToAdd} credits to ${selectedUser.name}`);
      setShowCreditsDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to add credits');
    }
  };

  const filteredUsers = users.filter(u => 
    u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPlanBadgeColor = (plan) => {
    switch (plan) {
      case 'unlimited': return 'bg-amber-100 text-amber-800';
      default: return 'bg-secondary text-muted-foreground';
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-primary-foreground" />
            </div>
            <h1 className="font-serif text-3xl font-medium text-primary">
              Admin Dashboard
            </h1>
          </div>
          <p className="text-muted-foreground">
            Manage users, view platform statistics, and monitor activity.
          </p>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {loading ? (
            Array(6).fill(0).map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))
          ) : (
            <>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <Card className="border-border" data-testid="stat-users">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-4 h-4 text-primary" />
                      <span className="text-xs text-muted-foreground">Users</span>
                    </div>
                    <p className="text-2xl font-serif font-medium">{stats?.total_users || 0}</p>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
                <Card className="border-border" data-testid="stat-quizzes">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4 text-emerald-500" />
                      <span className="text-xs text-muted-foreground">Quizzes</span>
                    </div>
                    <p className="text-2xl font-serif font-medium">{stats?.total_quizzes || 0}</p>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <Card className="border-border" data-testid="stat-students">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <GraduationCap className="w-4 h-4 text-blue-500" />
                      <span className="text-xs text-muted-foreground">Students</span>
                    </div>
                    <p className="text-2xl font-serif font-medium">{stats?.total_students || 0}</p>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
                <Card className="border-border" data-testid="stat-submissions">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-4 h-4 text-purple-500" />
                      <span className="text-xs text-muted-foreground">Submissions</span>
                    </div>
                    <p className="text-2xl font-serif font-medium">{stats?.total_submissions || 0}</p>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                <Card className="border-border" data-testid="stat-subscriptions">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Crown className="w-4 h-4 text-amber-500" />
                      <span className="text-xs text-muted-foreground">Paid Plans</span>
                    </div>
                    <p className="text-2xl font-serif font-medium">{stats?.active_subscriptions || 0}</p>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
                <Card className="border-border bg-emerald-50" data-testid="stat-revenue">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="w-4 h-4 text-emerald-600" />
                      <span className="text-xs text-emerald-700">Revenue (MTD)</span>
                    </div>
                    <p className="text-2xl font-serif font-medium text-emerald-700">
                      ${(stats?.revenue_this_month || 0).toFixed(2)}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            </>
          )}
        </div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-8"
        >
          <Card className="border-border">
            <CardHeader className="pb-2">
              <CardTitle className="font-serif text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                <Button onClick={() => navigate('/create')} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Create Quiz
                </Button>
                <Button variant="outline" onClick={() => navigate('/library')} className="gap-2">
                  <FileText className="w-4 h-4" />
                  View Library
                </Button>
                <Button variant="outline" onClick={() => navigate('/students')} className="gap-2">
                  <GraduationCap className="w-4 h-4" />
                  Manage Students
                </Button>
                <Button variant="outline" onClick={() => navigate('/assign')} className="gap-2">
                  <Settings className="w-4 h-4" />
                  Assignments
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Users Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
        >
          <Card className="border-border">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <CardTitle className="font-serif text-xl">All Users</CardTitle>
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                    data-testid="user-search"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {Array(5).fill(0).map((_, i) => (
                    <Skeleton key={i} className="h-16 rounded-lg" />
                  ))}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="users-table">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">User</th>
                        <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Plan</th>
                        <th className="text-center py-3 px-2 text-sm font-medium text-muted-foreground">Quizzes</th>
                        <th className="text-center py-3 px-2 text-sm font-medium text-muted-foreground">Students</th>
                        <th className="text-center py-3 px-2 text-sm font-medium text-muted-foreground">Credits</th>
                        <th className="text-right py-3 px-2 text-sm font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((u) => (
                        <tr key={u.id} className="border-b border-border/50 hover:bg-secondary/30">
                          <td className="py-3 px-2">
                            <div className="flex items-center gap-2">
                              {u.is_admin && <Shield className="w-4 h-4 text-primary" />}
                              <div>
                                <p className="font-medium text-sm">{u.name}</p>
                                <p className="text-xs text-muted-foreground">{u.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-2">
                            <Badge className={`${getPlanBadgeColor(u.subscription_plan)} capitalize`}>
                              {u.subscription_plan}
                            </Badge>
                          </td>
                          <td className="py-3 px-2 text-center text-sm">{u.quiz_count}</td>
                          <td className="py-3 px-2 text-center text-sm">{u.student_count}</td>
                          <td className="py-3 px-2 text-center text-sm">{u.quiz_credits || 0}</td>
                          <td className="py-3 px-2 text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setSelectedUser(u);
                                  setNewPlan(u.subscription_plan);
                                  setShowPlanDialog(true);
                                }}
                                data-testid={`edit-plan-${u.id}`}
                              >
                                <Crown className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setSelectedUser(u);
                                  setCreditsToAdd(10);
                                  setShowCreditsDialog(true);
                                }}
                                data-testid={`add-credits-${u.id}`}
                              >
                                <CreditCard className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {filteredUsers.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No users found matching "{searchTerm}"
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </main>

      {/* Update Plan Dialog */}
      <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Subscription Plan</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              Change plan for <strong>{selectedUser?.name}</strong> ({selectedUser?.email})
            </p>
            <Select value={newPlan} onValueChange={setNewPlan}>
              <SelectTrigger>
                <SelectValue placeholder="Select plan" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="unlimited">Unlimited ($27.99/mo)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPlanDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdatePlan}>Update Plan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Credits Dialog */}
      <Dialog open={showCreditsDialog} onOpenChange={setShowCreditsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Quiz Credits</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              Add bonus quiz credits to <strong>{selectedUser?.name}</strong>
            </p>
            <Input
              type="number"
              min="1"
              value={creditsToAdd}
              onChange={(e) => setCreditsToAdd(parseInt(e.target.value) || 0)}
              placeholder="Number of credits"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreditsDialog(false)}>Cancel</Button>
            <Button onClick={handleAddCredits}>Add Credits</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
